// EmailVerificationPending.jsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Box, Button, Typography, CircularProgress, Paper, Snackbar, Alert } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SendIcon from '@mui/icons-material/Send';
import { getAuth, sendEmailVerification } from 'firebase/auth';
import app from '../../firebase';

const auth = getAuth(app);

const EmailVerificationPending = () => {
  const { currentUser, checkEmailVerified, fetchUserType } = useAuth();
  const navigate = useNavigate();
  const [emailVerified, setEmailVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const verifyEmail = async () => {
      if (currentUser) {
        const isVerified = await checkEmailVerified(currentUser);
        setEmailVerified(isVerified);
        setLoading(false);
      } else {
        // If no user is logged in, we shouldn't show the check forever
        setLoading(false);
      }
    };

    verifyEmail();
  }, [currentUser, checkEmailVerified]);

  const handleRetry = async () => {
    setLoading(true);
    const isVerified = await checkEmailVerified(currentUser);
    setEmailVerified(isVerified);
    setLoading(false);
  };

  const handleResendEmail = async () => {
    if (!auth.currentUser) {
      setSnackbar({ open: true, message: 'Please log in again to resend verification email.', severity: 'error' });
      return;
    }

    setResending(true);
    try {
      // Custom action code settings for better email deliverability
      const actionCodeSettings = {
        url: 'https://tenantguidedtours.com/verify-email',
        handleCodeInApp: false,
      };

      await sendEmailVerification(auth.currentUser, actionCodeSettings);
      setSnackbar({
        open: true,
        message: 'Verification email sent! Please check your inbox and spam folder.',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error resending verification email:', error);
      if (error.code === 'auth/too-many-requests') {
        setSnackbar({
          open: true,
          message: 'Too many attempts. Please wait a few minutes before trying again.',
          severity: 'warning'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to send email. Please try again later.',
          severity: 'error'
        });
      }
    } finally {
      setResending(false);
    }
  };

  const handleContinue = async () => {
    try {
      const userType = await fetchUserType(currentUser.uid);
      if (userType === 'landlord' || userType === 'manager') {
        navigate('/agent-onboarding');
      } else {
        navigate('/onboarding');
      }
    } catch (error) {
      console.error('Error fetching user type:', error);
      alert('An error occurred. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        bgcolor="#f4f6f8"
      >
        <CircularProgress />
        <Typography variant="h6" mt={2}>
          Checking email verification status...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      bgcolor="#f4f6f8"
    >
      <Paper
        elevation={3}
        sx={{
          padding: '2rem',
          maxWidth: '500px',
          textAlign: 'center',
          borderRadius: '12px',
        }}
      >
        {!emailVerified ? (
          <>
            <EmailOutlinedIcon
              color="primary"
              sx={{ fontSize: '4rem', marginBottom: '1rem' }}
            />
            <Typography variant="h5" gutterBottom>
              Verify Your Email Address
            </Typography>
            <Typography variant="body1" gutterBottom>
              We've sent a verification link to{' '}
              <Typography component="span" fontWeight="bold">
                {currentUser?.email}
              </Typography>
              . Please check your inbox (and spam folder) to verify your account.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', marginTop: '1.5rem', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshOutlinedIcon />}
                onClick={handleRetry}
              >
                Check Again
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={resending ? <CircularProgress size={20} /> : <SendIcon />}
                onClick={handleResendEmail}
                disabled={resending}
              >
                {resending ? 'Sending...' : 'Resend Email'}
              </Button>
            </Box>
            <Typography variant="body2" sx={{ marginTop: '1rem', color: '#666' }}>
              Didn't receive the email? Check your spam folder or click "Resend Email".
            </Typography>
          </>
        ) : (
          <>
            <CheckCircleOutlineIcon
              color="success"
              sx={{ fontSize: '4rem', marginBottom: '1rem' }}
            />
            <Typography variant="h5" gutterBottom>
              Email Verification Successful
            </Typography>
            <Typography variant="body1" gutterBottom>
              Dear {currentUser.email},<br />
              Welcome to Tenant Guided Tours! We’re delighted to have you join our platform.
              Your email has been successfully verified, and you’re now ready to explore
              a seamless way to connect tenants and landlords. Click below to begin your journey.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              sx={{ marginTop: '1.5rem' }}
              onClick={handleContinue}
            >
              Get Started
            </Button>
          </>
        )}
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmailVerificationPending;


// import React, { useEffect, useState } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { useAuth } from '../../hooks/useAuth';
// import { Box, Button, Typography, CircularProgress, Paper } from '@mui/material';
// import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
// import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
// import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';

// const EmailVerificationPending = () => {
//   const { currentUser, checkEmailVerified, fetchUserType } = useAuth();
//   const navigate = useNavigate();
//   const [emailVerified, setEmailVerified] = useState(false);
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     const verifyEmail = async () => {
//       if (currentUser) {
//         const isVerified = await checkEmailVerified(currentUser);
//         setEmailVerified(isVerified);
//         setLoading(false);
//       }
//     };
//     verifyEmail();
//   }, [currentUser, checkEmailVerified]);

//   const handleRetry = async () => {
//     setLoading(true);
//     const isVerified = await checkEmailVerified(currentUser);

//     setEmailVerified(isVerified);
//     setLoading(false);

//     if (isVerified) {
//       try {
//         // Fetch user type to determine the appropriate onboarding page
//         const userType = await fetchUserType(currentUser.uid);

//         if (userType === 'landlord') {
//           navigate('/agent-onboarding'); // Redirect to agent onboarding for landlords
//         } else {
//           navigate('/onboarding'); // Redirect to standard onboarding for renters
//         }
//       } catch (error) {
//         console.error('Error fetching user type:', error);
//         alert('An error occurred. Please try again.');
//       }
//     }
//   };

//   if (loading) {
//     return (
//       <Box
//         display="flex"
//         flexDirection="column"
//         alignItems="center"
//         justifyContent="center"
//         minHeight="100vh"
//         bgcolor="#f4f6f8"
//       >
//         <CircularProgress />
//         <Typography variant="h6" mt={2}>
//           Checking email verification status...
//         </Typography>
//       </Box>
//     );
//   }

//   return (
//     <Box
//       display="flex"
//       alignItems="center"
//       justifyContent="center"
//       minHeight="100vh"
//       bgcolor="#f4f6f8"
//     >
//       <Paper
//         elevation={3}
//         sx={{
//           padding: '2rem',
//           maxWidth: '500px',
//           textAlign: 'center',
//           borderRadius: '12px',
//         }}
//       >
//         {!emailVerified ? (
//           <>
//             <EmailOutlinedIcon
//               color="primary"
//               sx={{ fontSize: '4rem', marginBottom: '1rem' }}
//             />
//             <Typography variant="h5" gutterBottom>
//               Verify Your Email Address
//             </Typography>
//             <Typography variant="body1" gutterBottom>
//               We’ve sent a verification link to{' '}
//               <Typography component="span" fontWeight="bold">
//                 {currentUser.email}
//               </Typography>
//               . Please check your inbox (and spam folder) to verify your account.
//             </Typography>
//             <Button
//               variant="contained"
//               color="primary"
//               startIcon={<RefreshOutlinedIcon />}
//               sx={{ marginTop: '1.5rem' }}
//               onClick={handleRetry}
//             >
//               Check Again
//             </Button>
//           </>
//         ) : (
//           <>
//             <CheckCircleOutlineIcon
//               color="success"
//               sx={{ fontSize: '4rem', marginBottom: '1rem' }}
//             />
//             <Typography variant="h5" gutterBottom>
//               Email Verified!
//             </Typography>
//             <Typography variant="body1" gutterBottom>
//               Redirecting you to your next step...
//             </Typography>
//           </>
//         )}
//       </Paper>
//     </Box>
//   );
// };

// export default EmailVerificationPending;

